/* ============================================================================
   Cart drawer v2 — the live v4 redesign (won the A/B; default since 2026-07-21).
   Fully namespaced so it coexists with the original <cart-drawer> / #CartDrawer,
   which still renders hidden as its dependency host + render trigger.
   Elements: <cart-drawer-v2>, <cart-drawer-items-v2>
   IDs:      #CartDrawer-v2*, Drawer-quantity-v2-N
   Section:  cart-drawer-v2  (sections/cart-drawer-v2.liquid)
   Reuses generic per-item elements (quantity-input, quantity-popover,
   cart-remove-button) from cart.js. Loads AFTER cart.js + cart-drawer.js.
   ========================================================================== */
(function () {
  if (typeof CartItems === 'undefined' || typeof CartDrawer === 'undefined') return;

  /* ---- drawer-items element (qty / remove / re-render), namespaced ---- */
  class CartDrawerItemsV2 extends CartItems {
    getSectionsToRender() {
      return [
        { id: 'CartDrawer-v2', section: 'cart-drawer-v2', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    onCartUpdate() {
      return fetch(`${routes.cart_url}?section_id=cart-drawer-v2`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const fresh = html.querySelector('#CartDrawer-v2');
          const current = document.querySelector('#CartDrawer-v2');
          if (fresh && current) {
            current.innerHTML = fresh.innerHTML;
            const overlay = document.querySelector('#CartDrawer-v2-Overlay');
            const drawer = document.querySelector('cart-drawer-v2');
            if (overlay && drawer) overlay.addEventListener('click', drawer.close.bind(drawer));
          }
          const freshDrawer = html.querySelector('cart-drawer-v2');
          const drawerEl = document.querySelector('cart-drawer-v2');
          if (freshDrawer && drawerEl) drawerEl.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
          document.dispatchEvent(new CustomEvent('cart-v2:refresh'));
        })
        .catch((e) => console.error(e));
    }

    // CartItems resets #Quantity-N, the cart page's id. When a quantity is refused (a one-unit
    // subscription line typed past max="1") the drawer input must go back to its value.
    resetQuantityInput(id) {
      const input = this.querySelector(`#Drawer-quantity-v2-${id}`);
      if (input) input.value = input.getAttribute('value');
      this.isEnterPressed = false;
    }

    enableLoading(line) {
      const items = document.getElementById('CartDrawer-v2-CartItems');
      if (items) items.classList.add('cart__items--disabled');
      this.querySelectorAll(`#CartDrawer-v2-Item-${line} .loading__spinner`).forEach((o) => o.classList.remove('hidden'));
      if (document.activeElement) document.activeElement.blur();
      if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
    }

    disableLoading(line) {
      const items = document.getElementById('CartDrawer-v2-CartItems');
      if (items) items.classList.remove('cart__items--disabled');
      this.querySelectorAll('.loading__spinner').forEach((o) => o.classList.add('hidden'));
    }

    updateLiveRegions(line, message) {
      const err = document.getElementById(`CartDrawer-v2-LineItemError-${line}`);
      if (err) {
        const t = err.querySelector('.cart-item__error-text');
        if (t) t.textContent = message;
      }
      if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);
      const status = document.getElementById('CartDrawer-v2-LiveRegionText');
      if (status) {
        status.setAttribute('aria-hidden', false);
        setTimeout(() => status.setAttribute('aria-hidden', true), 1000);
      }
    }

    updateQuantity(line, quantity, event, name, variantId) {
      const eventTarget = 'change';
      this.enableLoading(line);
      const body = JSON.stringify({
        line,
        quantity,
        sections: this.getSectionsToRender().map((s) => s.section),
        sections_url: window.location.pathname,
      });
      let cartApiSucceeded = false;
      fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => response.text())
        .then((state) => {
          const parsedState = JSON.parse(state);
          if (!parsedState.errors && !parsedState.status) cartApiSucceeded = true;
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-v2-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }
          if (parsedState.status) {
            this.updateLiveRegions(line, parsedState.description || parsedState.message || window.cartStrings.error);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const drawer = document.querySelector('cart-drawer-v2');
          if (drawer) drawer.classList.toggle('is-empty', parsedState.item_count === 0);

          this.getSectionsToRender().forEach((section) => {
            const target =
              document.getElementById(section.id)?.querySelector(section.selector) || document.getElementById(section.id);
            if (target) target.innerHTML = this.getSectionInnerHTML(parsedState.sections?.[section.section], section.selector);
          });

          // Compare server qty against the REQUESTED qty (not the live input) so rapid
          // +/- clicks don't surface a false "you can only add X" (race fix).
          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          const requestedQuantity = parseInt(quantity, 10);
          if (items.length === parsedState.items.length && !Number.isNaN(requestedQuantity) && updatedValue !== requestedQuantity) {
            message = typeof updatedValue === 'undefined' ? window.cartStrings.error : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
          this.updateLiveRegions(line, message);

          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        })
        .catch(() => {
          this.querySelectorAll('.loading__spinner').forEach((o) => o.classList.add('hidden'));
          if (!cartApiSucceeded) {
            const errors = document.getElementById('CartDrawer-v2-CartErrors');
            if (errors) errors.textContent = window.cartStrings.error;
          }
        })
        .finally(() => this.disableLoading(line));
    }
  }
  customElements.define('cart-drawer-items-v2', CartDrawerItemsV2);

  /* ---- drawer element (open/close/render), namespaced ---- */
  class CartDrawerV2 extends CartDrawer {
    constructor() {
      super();
      const overlay = this.querySelector('#CartDrawer-v2-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
    }
    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      setTimeout(() => this.classList.add('animate', 'active'));
      // Lock the background scroll on BOTH <html> and <body>. body-only
      // overflow:hidden doesn't reliably stop the viewport (which scrolls on <html>)
      // from scrolling behind the open drawer.
      document.documentElement.classList.add('overflow-hidden');
      document.body.classList.add('overflow-hidden');
    }
    close() {
      this.classList.remove('active');
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
      // Clean up any focus trap WITHOUT refocusing, then return focus to the opener
      // WITHOUT scrolling. The base close() does removeTrapFocus(activeElement) →
      // activeElement.focus() (no preventScroll) → the header cart icon scrolls into
      // view → the page jumps to the top when you close the drawer.
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      if (this.activeElement && typeof this.activeElement.focus === 'function') {
        this.activeElement.focus({ preventScroll: true });
      }
    }
    getSectionsToRender() {
      return [
        { id: 'cart-drawer-v2', selector: '#CartDrawer-v2' },
        { id: 'cart-icon-bubble' },
      ];
    }
    renderContents(parsedState) {
      this.classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
        if (!el) return;
        const html = parsedState.sections && parsedState.sections[section.id];
        if (!html) return;
        el.innerHTML = this.getSectionInnerHTML(html, section.selector);
      });
      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-v2-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        const checkoutBtn = document.getElementById('CartDrawer-v2-Checkout');
        if (checkoutBtn) checkoutBtn.removeAttribute('disabled');
        this.open();
        initBestsellersCarouselV2();
      });
    }
  }
  customElements.define('cart-drawer-v2', CartDrawerV2);

  /* ---- minus at qty 1 removes the line (v2-aware) ---- */
  document.addEventListener(
    'click',
    function (event) {
      const minusBtn = event.target.closest('button[name="minus"]');
      if (!minusBtn) return;
      const cartItems = minusBtn.closest('cart-drawer-items-v2');
      if (!cartItems || typeof cartItems.updateQuantity !== 'function') return;
      const qtyInput = (minusBtn.closest('quantity-input') || minusBtn.parentElement).querySelector('.quantity__input');
      if (!qtyInput) return;
      const current = parseInt(qtyInput.value, 10);
      const min = Math.max(1, parseInt(qtyInput.dataset.min || qtyInput.getAttribute('min') || '1', 10) || 1);
      if (!isNaN(current) && current <= min) {
        event.preventDefault();
        event.stopImmediatePropagation();
        cartItems.updateQuantity(qtyInput.dataset.index, 0, event);
      }
    },
    true
  );

  /* ---- "Pairs well" carousel (scoped to .cart-drawer-v2) ----
     Paging is DELEGATED (handler below) and the current index lives on the
     track itself (data-idx), NOT in a closure. So when the drawer re-renders
     (open / qty change / add) the buttons never get stranded without a
     listener — the previous bug. Dots are display:none in v2, so prev/next only. */
  function v2ApplyCarousel(track, prevBtn, nextBtn, total, idx) {
    idx = Math.max(0, Math.min(total - 1, idx));
    track.style.transform = 'translateX(-' + idx * 100 + '%)';
    track.dataset.idx = String(idx);
    if (prevBtn) prevBtn.disabled = idx <= 0;
    if (nextBtn) nextBtn.disabled = idx >= total - 1;
  }
  function initBestsellersCarouselV2(root) {
    const bestsellers = (root || document).querySelector('.cart-drawer-v2 .cart-drawer__bestsellers');
    if (!bestsellers) return;
    const track = bestsellers.querySelector('.cart-drawer__bestsellers-products');
    if (!track) return;
    const total = track.querySelectorAll('.cart-drawer__product-card').length;
    const prevBtn = bestsellers.querySelector('.cart-drawer__carousel-btn--prev');
    const nextBtn = bestsellers.querySelector('.cart-drawer__carousel-btn--next');
    if (total <= 1) {
      if (prevBtn) prevBtn.style.display = 'none';
      if (nextBtn) nextBtn.style.display = 'none';
      return;
    }
    if (prevBtn) prevBtn.style.display = '';
    if (nextBtn) nextBtn.style.display = '';
    v2ApplyCarousel(track, prevBtn, nextBtn, total, parseInt(track.dataset.idx || '0', 10) || 0);
  }
  /* delegated paging — one listener for the whole page, survives every re-render */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest(
      '.cart-drawer-v2 .cart-drawer__carousel-btn--prev, .cart-drawer-v2 .cart-drawer__carousel-btn--next'
    );
    if (!btn || btn.disabled) return;
    const bestsellers = btn.closest('.cart-drawer__bestsellers');
    const track = bestsellers && bestsellers.querySelector('.cart-drawer__bestsellers-products');
    if (!track) return;
    const total = track.querySelectorAll('.cart-drawer__product-card').length;
    if (total <= 1) return;
    const prevBtn = bestsellers.querySelector('.cart-drawer__carousel-btn--prev');
    const nextBtn = bestsellers.querySelector('.cart-drawer__carousel-btn--next');
    const cur = parseInt(track.dataset.idx || '0', 10) || 0;
    const dir = btn.classList.contains('cart-drawer__carousel-btn--next') ? 1 : -1;
    e.preventDefault();
    v2ApplyCarousel(track, prevBtn, nextBtn, total, cur + dir);
  });

  /* ---- upsell "Add" (delegated, CAPTURE phase) ----
     Runs in capture + stopImmediatePropagation so it PREEMPTS the original
     cart-drawer.js `.cart-drawer__add-to-cart` handler. That handler is registered
     first (bubble), so without this it fires first, disables the button, and
     refreshes the hidden #CartDrawer — then the v2 handler bails on `btn.disabled`
     and the v2 drawer never shows the added item. */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.cart-drawer-v2 .cart-drawer__add-to-cart');
    if (!btn) return;
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    e.stopImmediatePropagation();
    e.preventDefault();
    if (btn.disabled) return;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';
    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: parseInt(variantId), quantity: 1, sections: ['cart-drawer-v2', 'cart-icon-bubble'] }),
    })
      .then((res) => res.json())
      .then((data) => {
        btn.textContent = 'Added!';
        if (data.sections && data.sections['cart-drawer-v2']) {
          const doc = new DOMParser().parseFromString(data.sections['cart-drawer-v2'], 'text/html');
          const fresh = doc.querySelector('#CartDrawer-v2 .drawer__inner');
          const cur = document.querySelector('#CartDrawer-v2 .drawer__inner');
          if (fresh && cur) {
            cur.innerHTML = fresh.innerHTML;
            setTimeout(() => initBestsellersCarouselV2(), 50);
          }
          const bubble = data.sections['cart-icon-bubble'];
          if (bubble) {
            const bdoc = new DOMParser().parseFromString(bubble, 'text/html');
            const nb = bdoc.querySelector('.shopify-section');
            const cb = document.getElementById('cart-icon-bubble');
            if (nb && cb) cb.innerHTML = nb.innerHTML;
          }
        }
        setTimeout(() => { btn.disabled = false; btn.textContent = originalText; }, 1800);
      })
      .catch(() => { btn.disabled = false; btn.textContent = originalText; });
  }, true);

  /* ---- re-init the v2 carousel on open + on any cart update ---- */
  function v2CarouselSoon() { setTimeout(() => initBestsellersCarouselV2(), 60); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', v2CarouselSoon);
  else v2CarouselSoon();
  // Is v2 the drawer currently shown? True by default now; false only under ?sidecart=v1.
  function v2IsShownVariant() {
    const inner = document.getElementById('CartDrawer-v2');
    return !!inner && getComputedStyle(inner).display !== 'none';
  }
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      const drawerItems = document.querySelector('cart-drawer-items-v2');
      // Refresh the v2 drawer markup for adds that didn't originate inside it.
      if (drawerItems && event && event.source !== 'cart-items' && typeof drawerItems.onCartUpdate === 'function') {
        const refreshed = drawerItems.onCartUpdate();
        // An add from a product form (PDP add-to-cart / quick-add) should OPEN the v2
        // sidecart with the new item — product-form.js publishes source:'product-form'
        // on every add but never opens the v2 variant. Mirror what the original does.
        if (event.source === 'product-form' && v2IsShownVariant()) {
          const openV2 = () => { const d = document.querySelector('cart-drawer-v2'); if (d && typeof d.open === 'function') d.open(); };
          if (refreshed && typeof refreshed.then === 'function') refreshed.then(openV2).catch(openV2);
          else openV2();
        }
      }
      v2CarouselSoon();
    });
  }

  /* Only open/act on v2 when it's the SHOWN drawer — i.e. always, unless ?sidecart=v1
     hid #CartDrawer-v2. With the legacy drawer restored, opening v2 gives its
     full-screen `<cart-drawer-v2 class="drawer active">` element visibility:visible,
     and since it's position:fixed inset:0 z-9999 it captures every click OVER the
     control drawer → the control freezes (close button + click-outside dead). */
  function v2IsActiveVariant() {
    const inner = document.getElementById('CartDrawer-v2');
    return !!inner && getComputedStyle(inner).display !== 'none';
  }

  /* ---- SINGLE MIRROR POINT: CartDrawer.prototype.open ----
     Now that the legacy drawer is permanently display:none, anything that opens it
     would be invisible to the shopper. Rather than mirroring each caller one by one,
     hook the one funnel they all reach — CartDrawer.prototype.open — and redirect it
     to the refreshed v2 drawer. This covers every path at once:
       • renderContents()                  (product-form happy path, quick-add, cart.js)
       • tryRefreshCartAndOpenDrawer()     (product-form's ERROR fallback)
       • setHeaderCartIconAccessibility()  (header cart icon click / keydown)
       • openCartDrawer()                  (global helper, product cards, popups)
     Earlier versions hooked only renderContents, which missed the error fallback —
     and that fallback is exactly what runs when a 'cart-update' subscriber throws.
     No recursion: CartDrawerV2 overrides open() on its own prototype, so v2.open()
     never re-enters this wrapper. */
  if (CartDrawer.prototype && CartDrawer.prototype.open) {
    const _origOpen = CartDrawer.prototype.open;
    let v2RefreshInFlight = null;
    CartDrawer.prototype.open = function (triggeredBy) {
      const v2 = document.querySelector('cart-drawer-v2');
      if (!v2IsActiveVariant() || !v2 || typeof v2.open !== 'function') {
        return _origOpen.call(this, triggeredBy);
      }
      const show = () => {
        v2.open(triggeredBy);
        setTimeout(() => initBestsellersCarouselV2(), 60);
      };
      const v2Items = document.querySelector('cart-drawer-items-v2');
      if (!v2Items || typeof v2Items.onCartUpdate !== 'function') return show();
      // Refresh BEFORE showing, so an add-to-cart never flashes the pre-add cart.
      // (The header cart icon still feels instant: <cart-drawer-v2> inherits
      // setHeaderCartIconAccessibility from CartDrawer, so it binds the icon and
      // opens itself immediately in parallel with this refresh.)
      // Openers commonly fire in pairs — the legacy element and the v2 element both
      // bind that icon — so concurrent opens share ONE section fetch rather than
      // stacking a request per opener.
      if (!v2RefreshInFlight) {
        v2RefreshInFlight = v2Items.onCartUpdate().finally(() => {
          v2RefreshInFlight = null;
        });
      }
      v2RefreshInFlight.then(show).catch(show);
    };
  }

  /* ---- Checkout+ label re-case (v2): "CHECKOUT+ • $X" -> "Checkout+ • $X" ----
     Figma v4 keeps the SavedBy widget's native single-line "Checkout+ • $X" format —
     only the ALL-CAPS casing needs fixing. The widget sometimes replaces the whole
     button innerHTML (dropping our .cart-drawer__checkout-label/-sub spans), so this
     walks every text node under the button rather than assuming that markup survives.
     Case-SENSITIVE match on literal "CHECKOUT" + a no-op guard (skip when already
     correctly cased) so this can't re-trigger its own MutationObserver in a loop. */
  function formatCheckoutPlusButtonV2() {
    const btn = document.querySelector('#CartDrawer-v2-Checkout');
    if (!btn) return;
    const walker = document.createTreeWalker(btn, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      const next = node.nodeValue.replace(/CHECKOUT(\s*\+)/, 'Checkout$1');
      if (next !== node.nodeValue) node.nodeValue = next;
    }
  }
  const moV2 = new MutationObserver(() => formatCheckoutPlusButtonV2());
  function watchCheckoutV2() {
    // Observe the STABLE parent (.cart__ctas), not the button itself: SavedBy's widget
    // sometimes replaces the whole <button> node (not just its text), which would
    // detach an observer attached directly to it and silently stop catching updates.
    const parent = document.querySelector('#CartDrawer-v2 .cart__ctas') || document.querySelector('#CartDrawer-v2-Checkout')?.parentElement;
    if (!parent) return;
    formatCheckoutPlusButtonV2();
    moV2.disconnect();
    moV2.observe(parent, { childList: true, subtree: true, characterData: true });
  }
  // SavedBy's own widget script loads async and can take 1-2s+ to render/re-render the
  // button; back the observer with a short poll so a missed mutation record still self-heals.
  let checkoutPollCount = 0;
  const checkoutPollId = setInterval(() => {
    checkoutPollCount += 1;
    formatCheckoutPlusButtonV2();
    if (checkoutPollCount >= 12) clearInterval(checkoutPollId); // ~6s of polling at 500ms
  }, 500);
  document.addEventListener('cart-v2:refresh', watchCheckoutV2);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCheckoutV2);
  else watchCheckoutV2();
})();

/* QA / rollback toggle: ?sidecart=v1 restores the legacy drawer. v4 is the default
   now that it won the A/B (see the two rules at the top of cart-drawer-v2.css), so
   this param exists to eyeball the old design or to sanity-check a regression against
   it — NOT as a production path. It reproduces the old control state exactly: the
   <cart-drawer-v2> element stays in the DOM with only #CartDrawer-v2 hidden, which is
   what the control cohort was actually served. `display:block` on cart-drawer matches
   its computed value (position:fixed blockifies the custom element).
   Safe no-op without the param. */
try {
  if (new URLSearchParams(location.search).get('sidecart') === 'v1') {
    var __sidecartV1 = document.createElement('style');
    __sidecartV1.textContent = 'cart-drawer{display:block!important}#CartDrawer-v2{display:none!important}';
    (document.head || document.documentElement).appendChild(__sidecartV1);
  }
} catch (e) {}
